import { createSlice } from "@reduxjs/toolkit";
import { changeAllSdbSplashScreensStatus, parseFueFlags } from "@store/user/user-slice-helper";
import { fetchCurrentUser, getLoggedInUser, logoutUser, setFueFlags, updateCurrentUserProfile } from "@store/user/user-slice-thunk";

/** State of User members */

const initialState = {
  currentUser: null,
  loggedInUser: null,
  // At initial state, all splash screens are unseen. The status will update based on the fetched data from backend
  splashScreens: {
    sdb: changeAllSdbSplashScreensStatus("unseen"),
    other: {}
  },
  updatedEmail: undefined,
  fetching: {
    isFetchingCurrentUser: false,
    loggedInUserFetchingStatus: "uninitialized",
    isSettingFueFlags: false,
    isLoggingOut: false
  }
};

/**
 * Slice to access state of loaded user members
 */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentUser(state, action) {
      state.currentUser = action.payload;
    },
    setLoggedInUser(state, action) {
      state.loggedInUser = action.payload;
    },
    setUpdatedEmail(state, action) {
      state.updatedEmail = action.payload;
    },
    /** Set the status of the splashscreen topic to the provided one */
    setSplashScreenStatus(state, action) {
      state.splashScreens.sdb[action.payload.topic] = action.payload.status;
    },
    /** Reset the status of all the splashscreen topics to unseen */
    resetSplashScreenStatus(state) {
      state.splashScreens.sdb = changeAllSdbSplashScreensStatus("unseen");
    },
    /** Set the fetching status for logged in user */
    setLoggedInUserFetchingStatus(state, action) {
      state.fetching.loggedInUserFetchingStatus = action.payload;
    },
    resetUserState: () => initialState
  },
  extraReducers(builder) {
    builder.addCase(fetchCurrentUser.pending, state => {
      state.fetching.isFetchingCurrentUser = true;
    }).addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.fetching.isFetchingCurrentUser = false;
      state.currentUser = action.payload;
    }).addCase(fetchCurrentUser.rejected, state => {
      state.fetching.isFetchingCurrentUser = false;
    }).addCase(getLoggedInUser.pending, state => {
      state.fetching.loggedInUserFetchingStatus = "pending";
    }).addCase(getLoggedInUser.fulfilled, (state, action) => {
      state.loggedInUser = action.payload;
      state.fetching.loggedInUserFetchingStatus = "succeeded";
      if (action.payload.fueFlags) {
        state.splashScreens = parseFueFlags(state.splashScreens, action.payload.fueFlags);
      }
    }).addCase(getLoggedInUser.rejected, state => {
      state.fetching.loggedInUserFetchingStatus = "rejected";
    }).addCase(setFueFlags.pending, state => {
      state.fetching.isSettingFueFlags = true;
    }).addCase(setFueFlags.fulfilled, (state, action) => {
      state.fetching.isSettingFueFlags = false;
      state.splashScreens = parseFueFlags(state.splashScreens, action.payload.updatedValue);
    }).addCase(setFueFlags.rejected, (state, _action) => {
      state.fetching.isSettingFueFlags = false;
    }).addCase(updateCurrentUserProfile.fulfilled, (state, action) => {
      const {
        firstName,
        lastName,
        thumbnailUrl,
        email
      } = action.payload;
      const basicUserInfo = {
        firstName,
        lastName,
        thumbnailUrl,
        email
      };
      if (state.currentUser) {
        Object.assign(state.currentUser, basicUserInfo);
      }
      if (state.loggedInUser) {
        Object.assign(state.loggedInUser, basicUserInfo);
      }
    }).addCase(logoutUser.pending, state => {
      state.fetching.isLoggingOut = true;
    }).addCase(logoutUser.fulfilled, state => {
      state.fetching.isLoggingOut = false;
      state.loggedInUser = null;
    }).addCase(logoutUser.rejected, state => {
      state.fetching.isLoggingOut = false;
    });
  }
});
export const {
  setCurrentUser,
  setLoggedInUser,
  setUpdatedEmail,
  setSplashScreenStatus,
  resetSplashScreenStatus,
  resetUserState,
  setLoggedInUserFetchingStatus
} = userSlice.actions;
export const userReducer = userSlice.reducer;